/**
 * Phase 2 MM9 — Project Studio: list + create + detail tabs
 * (overview / brief / references / generation-brief / activity).
 * All real API calls; honest "no image generation" labeling.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createGenerationBrief, createStudioProject, getGenerationBriefs,
  getProspects, getStudioActivity, getStudioBrief, getStudioProject,
  getStudioProjects, getStudioReferences, putStudioBrief,
  setStudioProjectStatus, uploadStudioReference,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import { studioStrings } from "../i18n/studioStrings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";
import { ArtworkTab } from "./ArtworkTab";
import { ComplianceTab } from "./ComplianceTab";
import { ProductionQaTab } from "./ProductionQaTab";
import { SalesPackageTab } from "./SalesPackageTab";
import { SupplierRfqTab } from "./SupplierRfqTab";
import { VisualConceptsTab } from "./VisualConceptsTab";

type Row = Record<string, unknown>;
const PROJECT_TYPES = ["restaurant_delivery_packaging", "food_product_packaging",
  "beverage_packaging", "beauty_packaging", "supplement_packaging",
  "luxury_gift_packaging", "ecommerce_shipping_packaging",
  "export_market_adaptation", "other"];
const OBJECTS = ["burger_box", "pizza_box", "fries_box", "cup", "sleeve",
  "delivery_bag", "sticker", "wrap_paper", "pouch", "jar_label",
  "bottle_label", "rigid_box", "folding_carton", "mailer_box", "other"];
const REF_TYPES = ["logo", "menu", "existing_packaging_photo", "brand_photo",
  "competitor_reference", "moodboard", "dieline", "ingredient_or_label_text",
  "supplier_spec", "brand_guideline", "other"];
const BRIEF_KEYS = ["brand_name", "business_description", "product_or_menu_items",
  "target_customer", "brand_personality", "price_positioning", "desired_style",
  "colors", "typography_direction", "packaging_goals", "functional_constraints",
  "delivery_constraints", "materials_preference", "sustainability_preference",
  "mandatory_text", "optional_text", "prohibited_words_or_claims",
  "required_languages", "competitor_reference_notes", "team_notes",
  "internal_sales_angle", "intended_output_package"];
const STATUSES = ["draft", "brief_in_progress", "references_collected",
  "ready_for_visual_concepts", "visual_concepts_in_progress", "concept_selected",
  "artwork_in_progress", "compliance_review", "supplier_rfq",
  "presentation_ready", "presented_to_client", "won", "lost"];

export function ProjectStudioPage(): JSX.Element {
  const [locale] = useLocale();
  const t = studioStrings(locale);
  const navigate = useNavigate();
  const { projectId, tab = "overview" } = useParams();
  const [items, setItems] = useState<Row[]>([]);
  const [prospects, setProspects] = useState<Row[]>([]);
  const [detail, setDetail] = useState<Row | null>(null);
  const [brief, setBrief] = useState<Record<string, string>>({});
  const [completeness, setCompleteness] = useState<number | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [refs, setRefs] = useState<Row[]>([]);
  const [genBriefs, setGenBriefs] = useState<Row[]>([]);
  const [activity, setActivity] = useState<Row[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", project_type: PROJECT_TYPES[0],
    prospect_id: "", target_market: "AE", objects: [] as string[] });

  const refreshList = useCallback(async () => {
    const [pr, ps] = await Promise.all([getStudioProjects(), getProspects()]);
    if (pr.outcome === "ok") setItems(pr.data?.items ?? []);
    else setNotice(pr.status === 403 ? t.permissionDenied : t.loadError);
    if (ps.outcome === "ok") setProspects(ps.data?.items ?? []);
  }, [t]);

  const refreshDetail = useCallback(async (id: string) => {
    const d = await getStudioProject(id);
    if (d.outcome === "ok") setDetail(d.data as Row);
    const b = await getStudioBrief(id);
    if (b.outcome === "ok") {
      const bj = ((b.data?.item as Row | null)?.brief_json ?? {}) as Record<string, unknown>;
      setBrief(Object.fromEntries(Object.entries(bj).map(([k, v]) =>
        [k, Array.isArray(v) ? v.join(", ") : String(v ?? "")])));
      setCompleteness(((b.data?.item as Row | null)?.completeness as number) ?? null);
    }
    const [r, g, a] = await Promise.all([
      getStudioReferences(id), getGenerationBriefs(id), getStudioActivity(id)]);
    if (r.outcome === "ok") setRefs(r.data?.items ?? []);
    if (g.outcome === "ok") setGenBriefs(g.data?.items ?? []);
    if (a.outcome === "ok") setActivity(a.data?.items ?? []);
  }, []);

  useEffect(() => {
    if (projectId) void refreshDetail(projectId);
    else void refreshList();
  }, [projectId, refreshDetail, refreshList]);

  async function onCreate(): Promise<void> {
    const r = await createStudioProject({
      name: form.name, project_type: form.project_type,
      prospect_id: form.prospect_id || null,
      target_market: form.target_market,
      packaging_objects: form.objects, output_languages: ["en", "ar"] });
    if (r.outcome === "ok") navigate(`/app/projects/${(r.data?.item as Row).id}`);
    else setNotice(r.status === 403 ? t.permissionDenied : r.errorMessage);
  }

  async function onSaveBrief(): Promise<void> {
    if (!projectId) return;
    const payload: Record<string, unknown> = {};
    for (const k of BRIEF_KEYS) {
      const v = (brief[k] ?? "").trim();
      if (!v) continue;
      payload[k] = ["product_or_menu_items", "colors", "required_languages",
        "prohibited_words_or_claims"].includes(k)
        ? v.split(",").map((x) => x.trim()).filter(Boolean) : v;
    }
    const r = await putStudioBrief(projectId, payload);
    if (r.outcome === "ok") {
      setCompleteness(r.data?.completeness ?? null);
      setMissing(r.data?.missing_fields ?? []);
      setNotice(null);
    } else setNotice(r.status === 403 ? t.permissionDenied : r.errorMessage);
  }

  // ---------- list mode ----------
  if (!projectId) {
    return (
      <AppLayout>
        <h1>{t.projectsTitle}</h1>
        {notice ? <p role="alert" className="po-error">{notice}</p> : null}
        <section className="po-row" data-testid="project-create">
          <input placeholder={t.nameLabel} value={form.name} data-testid="project-name"
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select value={form.project_type} data-testid="project-type"
                  onChange={(e) => setForm({ ...form, project_type: e.target.value })}>
            {PROJECT_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={form.prospect_id}
                  onChange={(e) => setForm({ ...form, prospect_id: e.target.value })}>
            <option value="">—</option>
            {prospects.map((p) => (
              <option key={String(p.id)} value={String(p.id)}>{String(p.name)}</option>))}
          </select>
          <select multiple value={form.objects} data-testid="project-objects"
                  onChange={(e) => setForm({ ...form,
                    objects: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
            {OBJECTS.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <Button variant="primary" onClick={() => void onCreate()}>{t.newProject}</Button>
        </section>
        {items.length === 0 ? <p data-testid="projects-empty">{t.emptyList}</p> : (
          <table className="po-table" data-testid="projects-table">
            <thead><tr><th>{t.nameLabel}</th><th>{t.typeLabel}</th>
              <th>{t.statusLabel}</th><th>Prospect</th></tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={String(p.id)}
                    onClick={() => navigate(`/app/projects/${p.id}`)}>
                  <td>{String(p.name)}</td><td>{String(p.project_type)}</td>
                  <td><StatusPill tone="info" label={String(p.status)} /></td>
                  <td>{String(p.prospect_name ?? "—")}</td>
                </tr>))}
            </tbody>
          </table>)}
      </AppLayout>
    );
  }

  // ---------- detail mode ----------
  const project = (detail?.item ?? null) as Row | null;
  return (
    <AppLayout>
      <h1>{project ? String(project.name) : "…"}</h1>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}
      <nav className="po-tabs">
        {(["overview", "brief", "references", "generation-brief", "visuals", "artwork", "compliance", "suppliers", "sales", "production", "activity"] as const)
          .map((k) => (
            <button key={k} type="button" aria-pressed={tab === k}
                    className={tab === k ? "po-tab po-tab--active" : "po-tab"}
                    onClick={() => navigate(`/app/projects/${projectId}${k === "overview" ? "" : `/${k}`}`)}>
              {{ overview: t.overviewTab, brief: t.briefTab,
                 references: t.referencesTab, "generation-brief": t.generationTab,
                 visuals: t.visualsTab, artwork: t.artworkTab,
                 compliance: t.complianceTab, suppliers: t.supplierTab,
                 sales: t.salesTab, production: t.productionTab,
                 activity: t.activityTab }[k]}
            </button>))}
      </nav>

      {tab === "overview" && detail ? (
        <section data-testid="project-overview">
          <p><StatusPill tone="info" label={String(project?.status)} />{" "}
             {t.completeness}: {String(detail.brief_completeness)}% ·{" "}
             {t.referencesTab}: {String(detail.references_count)} ·{" "}
             {t.readiness}: {String(detail.latest_generation_readiness ?? "—")}</p>
          <p><strong>{t.nextAction}:</strong> {String(detail.next_recommended_action)}</p>
          <select value={String(project?.status)} data-testid="project-status"
                  onChange={(e) => projectId &&
                    void setStudioProjectStatus(projectId, e.target.value)
                      .then(() => refreshDetail(projectId))}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </section>) : null}

      {tab === "brief" ? (
        <section data-testid="project-brief">
          {completeness !== null ? (
            <p>{t.completeness}: <strong>{completeness}%</strong>
              {missing.length ? <> · {t.missingFields}: {missing.slice(0, 6).join(", ")}</> : null}</p>) : null}
          <div className="po-columns">
            {BRIEF_KEYS.map((k) => (
              <label key={k} className="po-field">
                <span className="po-field__label">{k.replaceAll("_", " ")}</span>
                <input value={brief[k] ?? ""} data-testid={`brief-${k}`}
                       onChange={(e) => setBrief({ ...brief, [k]: e.target.value })} />
              </label>))}
          </div>
          <Button variant="primary" onClick={() => void onSaveBrief()}>{t.save}</Button>
        </section>) : null}

      {tab === "references" ? (
        <section data-testid="project-references">
          <ReferenceUploader projectId={projectId!} t={t}
                             onDone={() => void refreshDetail(projectId!)} />
          {refs.length === 0 ? <p>{t.emptyList}</p> : (
            <ul>{refs.map((r) => (
              <li key={String(r.id)}>
                [{String(r.reference_type)}] {String(r.file_name ?? "")} —{" "}
                <StatusPill tone={r.status === "processed" ? "ok" : "info"}
                            label={String(r.status)} /> {String(r.notes ?? "")}
              </li>))}</ul>)}
        </section>) : null}

      {tab === "generation-brief" ? (
        <section data-testid="project-generation">
          <p className="po-disclaimer">{t.noImagePhase}</p>
          <Button variant="primary" onClick={() => projectId &&
            void createGenerationBrief(projectId).then(() => refreshDetail(projectId))}>
            {t.createGenerationBrief}
          </Button>
          <ul>
            {genBriefs.map((g) => (
              <li key={String(g.id)}>
                v{String(g.version)} — {t.readiness}: {String(g.readiness)}%
                {g.artifact_id ? <> · <a href={`/app/artifacts?focus=${g.artifact_id}`}>artifact</a></> : null}
                {Array.isArray(g.missing_inputs) && (g.missing_inputs as string[]).length
                  ? <> · {t.missingFields}: {(g.missing_inputs as string[]).slice(0, 4).join(", ")}</> : null}
              </li>))}
          </ul>
        </section>) : null}

      {tab === "visuals" && projectId ? (
        <VisualConceptsTab projectId={projectId} t={t} />) : null}

      {tab === "artwork" && projectId ? (
        <ArtworkTab projectId={projectId} t={t} />) : null}

      {tab === "compliance" && projectId ? (
        <ComplianceTab projectId={projectId} t={t} />) : null}

      {tab === "suppliers" && projectId ? (
        <SupplierRfqTab projectId={projectId} t={t} />) : null}

      {tab === "sales" && projectId ? (
        <SalesPackageTab projectId={projectId} t={t} />) : null}

      {tab === "production" && projectId ? (
        <ProductionQaTab projectId={projectId} t={t} />) : null}

      {tab === "activity" ? (
        <section data-testid="project-activity">
          {activity.length === 0 ? <p>{t.emptyList}</p> : (
            <ul>{activity.map((a) => (
              <li key={String(a.id)}>{String(a.created_at).slice(0, 16)} —{" "}
                {String(a.event_type)}</li>))}</ul>)}
        </section>) : null}
    </AppLayout>
  );
}

function ReferenceUploader({ projectId, t, onDone }: {
  projectId: string; t: ReturnType<typeof studioStrings>; onDone: () => void;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [refType, setRefType] = useState(REF_TYPES[1]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="po-row">
      <input type="file" data-testid="reference-file"
             accept=".pdf,.docx,.txt,.csv,.xlsx,.json,.png,.jpg,.jpeg,.webp"
             onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <select value={refType} data-testid="reference-type"
              onChange={(e) => setRefType(e.target.value)}>
        {REF_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <input placeholder={t.notesLabel} value={notes}
             onChange={(e) => setNotes(e.target.value)} />
      <Button variant="secondary" disabled={!file} onClick={async () => {
        if (!file) return;
        const r = await uploadStudioReference(projectId, file, refType, notes || undefined);
        setErr(r.outcome === "ok" ? null : r.errorMessage);
        if (r.outcome === "ok") { setFile(null); setNotes(""); onDone(); }
      }}>{t.uploadReference}</Button>
      {err ? <span role="alert" className="po-error">{err}</span> : null}
    </div>
  );
}
